import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
} from '@mui/material';
import { TableChart, FileDownload, Search } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import PageTitle from './layout/PageTitle';
import { useGetReportingsQuery } from '../../modules/reportingTournees/reportingTournees.api';
import { reportingTourneesService } from '../../modules/reportingTournees/reportingTournees.service';
import { COLUMNS, NB_COLS, sums } from './reportingTournees/reportingHelpers';
import DayBlock from './reportingTournees/DayBlock';
import TotalsLavages from './reportingTournees/TotalsLavages';
import ReportingTableSkeleton from './reportingTournees/ReportingTableSkeleton';
import ReportingInterventionsModal from './reportingTournees/ReportingInterventionsModal';

const FILTERS_STORAGE_KEY = 'reportingTournees:filters';

function loadSavedFilters() {
  try {
    const s = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || 'null');
    if (s && s.dateFrom && s.dateTo) return s;
  } catch (e) {
    /* ignore */
  }
  return null;
}

export default function ReportingTourneesPage() {
  const today = new Date();
  const saved = loadSavedFilters();
  const initFrom = saved?.dateFrom ?? format(startOfMonth(today), 'yyyy-MM-dd');
  const initTo = saved?.dateTo ?? format(endOfMonth(today), 'yyyy-MM-dd');

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [search, setSearch] = useState(saved?.search ?? '');
  // Dates réellement utilisées par la requête : mises à jour uniquement au clic / Entrée.
  const [applied, setApplied] = useState({ dateFrom: initFrom, dateTo: initTo });
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState({ open: false, date: null, campagneId: null, tourneeLibelle: null });

  // Persistance des filtres (restaurés au retour sur la page).
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ dateFrom, dateTo, search }));
  }, [dateFrom, dateTo, search]);

  const applyDates = () => setApplied({ dateFrom, dateTo });
  const openDetail = (row) => setDetail({ open: true, date: row.date, campagneId: row.campagneId, tourneeLibelle: row.tourneeLibelle });

  const { data = [], isFetching, isError, error } = useGetReportingsQuery(applied);

  const filtered = search.trim()
    ? data.filter((r) => {
        const q = search.toLowerCase();
        return (
          (r.clientLibelle ?? '').toLowerCase().includes(q) ||
          (r.agenceLibelle ?? '').toLowerCase().includes(q) ||
          (r.equipage ?? '').toLowerCase().includes(q) ||
          (r.tourneeLibelle ?? '').toLowerCase().includes(q)
        );
      })
    : data;

  const byDate = {};
  filtered.forEach((r) => {
    (byDate[r.date] ??= []).push(r);
  });
  const dates = Object.keys(byDate).sort(); // ASC (chronologique)

  const grand = sums(filtered);

  const handleExport = () => {
    setExporting(true);
    reportingTourneesService.exportCsv(applied).finally(() => setExporting(false));
  };

  return (
    <Box sx={{ p: 2 }}>
      <PageTitle title="Reporting tournées" icon={<TableChart sx={{ mr: 1, verticalAlign: 'middle' }} />} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid>
            <TextField
              label="Du"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyDates(); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid>
            <TextField
              label="Au"
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyDates(); }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid>
            <Button variant="contained" startIcon={<Search />} onClick={applyDates}>
              Appliquer
            </Button>
          </Grid>
          <Grid sx={{ flex: 1 }}>
            <TextField
              label="Recherche (client / agence / équipe / tournée)"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownload />}
              onClick={handleExport}
              disabled={exporting}
            >
              Exporter CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {isFetching && <ReportingTableSkeleton />}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur lors du chargement des données : {error?.data?.message || error?.error || 'Erreur inconnue'}
        </Alert>
      )}

      {!isFetching && !isError && (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 210px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      backgroundColor: '#213067',
                      color: 'white',
                      fontWeight: 'bold',
                      width: col.width,
                      textAlign: col.align || 'left',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={NB_COLS} align="center" sx={{ py: 4, color: '#888' }}>
                    Aucune donnée pour cette période
                  </TableCell>
                </TableRow>
              )}

              {dates.map((date) => (
                <DayBlock key={date} date={date} rows={byDate[date]} onShowDetail={openDetail} />
              ))}

              {/* Total général */}
              {dates.length > 1 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ backgroundColor: '#213067', color: 'white', fontWeight: 'bold', fontSize: 13 }}>
                    Total général ({filtered.length} tournée{filtered.length > 1 ? 's' : ''})
                  </TableCell>
                  <TableCell sx={{ backgroundColor: '#213067', fontWeight: 'bold', textAlign: 'center' }}>
                    <TotalsLavages t={grand} color="#B0BEC5" />
                  </TableCell>
                  <TableCell colSpan={3} sx={{ backgroundColor: '#213067' }} />
                  <TableCell colSpan={4} sx={{ backgroundColor: '#213067' }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ReportingInterventionsModal
        open={detail.open}
        date={detail.date}
        campagneId={detail.campagneId}
        tourneeLibelle={detail.tourneeLibelle}
        onClose={() => setDetail((d) => ({ ...d, open: false }))}
      />
    </Box>
  );
}
